import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { projectSettingsApi } from '@/api/projectSettings'
import { useTenant } from '@/contexts/TenantContext'
import type { SpendingCap } from '@/types'
import { invalidateWorkflowTriggerSchemas } from './useWorkflowDefinitions'

const CLAUDE_AUTH_AVAILABILITY_KEY_PREFIX = ['project', 'claude-auth', 'availability'] as const

export function invalidateClaudeAuthAvailability(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: CLAUDE_AUTH_AVAILABILITY_KEY_PREFIX })
}

function useClaudeApiKeyQueryKey(projectId: string | undefined) {
  const { currentTenant } = useTenant()
  return ['project', 'settings', 'claude-api-key', currentTenant?.id ?? '', projectId ?? ''] as const
}

export function useProjectClaudeApiKey(projectId: string | undefined, enabled = true) {
  const queryKey = useClaudeApiKeyQueryKey(projectId)
  return useQuery({
    queryKey,
    queryFn: () => projectSettingsApi.getClaudeApiKey(projectId!),
    enabled: enabled && !!projectId,
  })
}

export function useSaveProjectClaudeApiKey(projectId: string | undefined) {
  const queryClient = useQueryClient()
  const queryKey = useClaudeApiKeyQueryKey(projectId)
  return useMutation({
    mutationFn: (apiKey: string) => projectSettingsApi.saveClaudeApiKey(projectId!, apiKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      invalidateClaudeAuthAvailability(queryClient)
      invalidateWorkflowTriggerSchemas(queryClient)
    },
  })
}

export function useDeleteProjectClaudeApiKey(projectId: string | undefined) {
  const queryClient = useQueryClient()
  const queryKey = useClaudeApiKeyQueryKey(projectId)
  return useMutation({
    mutationFn: () => projectSettingsApi.deleteClaudeApiKey(projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      invalidateClaudeAuthAvailability(queryClient)
      invalidateWorkflowTriggerSchemas(queryClient)
    },
  })
}

function useClaudeAuthAvailabilityQueryKey(projectId: string | undefined) {
  const { currentTenant } = useTenant()
  return [...CLAUDE_AUTH_AVAILABILITY_KEY_PREFIX, currentTenant?.id ?? '', projectId ?? ''] as const
}

export function useProjectClaudeAuthAvailability(projectId: string | undefined, enabled = true) {
  const queryKey = useClaudeAuthAvailabilityQueryKey(projectId)
  return useQuery({
    queryKey,
    queryFn: () => projectSettingsApi.getClaudeAuthAvailability(projectId!),
    enabled: enabled && !!projectId,
  })
}

/* ── Copilot ── */

const COPILOT_AUTH_AVAILABILITY_KEY_PREFIX = ['project', 'copilot-auth', 'availability'] as const

export function invalidateCopilotAuthAvailability(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: COPILOT_AUTH_AVAILABILITY_KEY_PREFIX })
}

function useCopilotApiKeyQueryKey(projectId: string | undefined) {
  const { currentTenant } = useTenant()
  return ['project', 'settings', 'copilot-api-key', currentTenant?.id ?? '', projectId ?? ''] as const
}

export function useProjectCopilotApiKey(projectId: string | undefined, enabled = true) {
  const queryKey = useCopilotApiKeyQueryKey(projectId)
  return useQuery({
    queryKey,
    queryFn: () => projectSettingsApi.getCopilotApiKey(projectId!),
    enabled: enabled && !!projectId,
  })
}

export function useSaveProjectCopilotApiKey(projectId: string | undefined) {
  const queryClient = useQueryClient()
  const queryKey = useCopilotApiKeyQueryKey(projectId)
  return useMutation({
    mutationFn: (apiKey: string) => projectSettingsApi.saveCopilotApiKey(projectId!, apiKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      invalidateCopilotAuthAvailability(queryClient)
      invalidateWorkflowTriggerSchemas(queryClient)
    },
  })
}

export function useDeleteProjectCopilotApiKey(projectId: string | undefined) {
  const queryClient = useQueryClient()
  const queryKey = useCopilotApiKeyQueryKey(projectId)
  return useMutation({
    mutationFn: () => projectSettingsApi.deleteCopilotApiKey(projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      invalidateCopilotAuthAvailability(queryClient)
      invalidateWorkflowTriggerSchemas(queryClient)
    },
  })
}

function useCopilotAuthAvailabilityQueryKey(projectId: string | undefined) {
  const { currentTenant } = useTenant()
  return [...COPILOT_AUTH_AVAILABILITY_KEY_PREFIX, currentTenant?.id ?? '', projectId ?? ''] as const
}

export function useProjectCopilotAuthAvailability(projectId: string | undefined, enabled = true) {
  const queryKey = useCopilotAuthAvailabilityQueryKey(projectId)
  return useQuery({
    queryKey,
    queryFn: () => projectSettingsApi.getCopilotAuthAvailability(projectId!),
    enabled: enabled && !!projectId,
  })
}

/* ── SpendingCap ── */

function useClaudeSpendingCapQueryKey(projectId: string | undefined) {
  const { currentTenant } = useTenant()
  return ['project', 'settings', 'spending-cap', currentTenant?.id ?? '', projectId ?? ''] as const
}

export function useProjectClaudeSpendingCap(projectId: string | undefined, enabled = true) {
  const queryKey = useClaudeSpendingCapQueryKey(projectId)
  return useQuery<SpendingCap>({
    queryKey,
    queryFn: () => projectSettingsApi.getClaudeSpendingCap(projectId!),
    enabled: enabled && !!projectId,
  })
}

export function useSetProjectClaudeSpendingCap(projectId: string | undefined) {
  const queryClient = useQueryClient()
  const queryKey = useClaudeSpendingCapQueryKey(projectId)
  return useMutation({
    mutationFn: (capUsd: number | null) => projectSettingsApi.setClaudeSpendingCap(projectId!, capUsd),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: ['analytics', 'token-usage', 'claude', 'by-workflow', projectId ?? ''] })
      queryClient.invalidateQueries({ queryKey: ['analytics', 'token-usage', 'copilot', 'by-workflow', projectId ?? ''] })
    },
  })
}

/* ── Copilot SpendingCap ── */

function useCopilotSpendingCapQueryKey(projectId: string | undefined) {
  const { currentTenant } = useTenant()
  return ['project', 'settings', 'copilot-spending-cap', currentTenant?.id ?? '', projectId ?? ''] as const
}

export function useProjectCopilotSpendingCap(projectId: string | undefined, enabled = true) {
  const queryKey = useCopilotSpendingCapQueryKey(projectId)
  return useQuery<SpendingCap>({
    queryKey,
    queryFn: () => projectSettingsApi.getCopilotSpendingCap(projectId!),
    enabled: enabled && !!projectId,
  })
}

export function useSetProjectCopilotSpendingCap(projectId: string | undefined) {
  const queryClient = useQueryClient()
  const queryKey = useCopilotSpendingCapQueryKey(projectId)
  return useMutation({
    mutationFn: (capUsd: number | null) => projectSettingsApi.setCopilotSpendingCap(projectId!, capUsd),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: ['analytics', 'token-usage', 'copilot', 'by-workflow', projectId ?? ''] })
    },
  })
}
